import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store, createSelector } from '@ngrx/store';

import { MenuItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TimelineModule } from 'primeng/timeline';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';

import { currentTimeSeconds } from 'src/app/time';
import { LoadingState } from 'src/app/utilities/state.util';

import { dayjs } from '../../core/dayjs/dayjs';
import { brandingFeature, createConfigSelector } from '../../state/branding.store';
import { formatDateTimeWithTimezone } from '../job-helpers';
import { jobToolFeature, selectJob } from '../job-tool.reducer';

import { JobActivityActions } from './job-activity.actions';
import { EditableComment } from './job-activity.state';
import { JobAddCommentComponent } from './job-add-comment/job-add-comment.component';
import { JobCommentComponent } from './job-comment/job-comment.component';

const selectHeartbeatInfo = createSelector(
  selectJob,
  (job) => ({
    lastHeartbeat: job?.lastHeartbeat,
    nextHeartbeat: job?.nextHeartbeat,
    aiDisabled: job?.aiDisabled,
    heartbeatMessage: job?.heartbeatMessage,
    heartbeatCost: job?.heartbeatCost,
    heartbeatCount: job?.heartbeatCount,
    jobId: job?.id,
    timezone: job?.zone?.timezone,
  })
);

const selectHeartbeatEnabled = createConfigSelector('JOBSHEARTBEAT_ENABLED' as any, false);

const selectNonDeletedComments = createSelector(
  jobToolFeature.selectComments,
  brandingFeature.selectCommentModes,
  (comments, modes) => {
    return comments
      .filter((c) => c.callState !== LoadingState.DELETING)
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((c) => ({
        ...c,
        mode: modes.find((m) => m.id === c.modeId),
      }));
  },
);

@Component({
  selector: 'app-job-activity',
  standalone: true,
  imports: [
    CommonModule,
    JobCommentComponent,
    JobAddCommentComponent,
    TimelineModule,
    BadgeModule,
    ButtonModule,
    MenuModule,
    TooltipModule,
  ],
  templateUrl: './job-activity.component.html',
  styleUrl: './job-activity.component.scss'
})
export class JobActivityComponent implements OnDestroy {

  private destroy$ = new Subject<void>();
  heartbeatLoading = false;

  public viewModel$ = this.store.select(createSelector({
    comments: selectNonDeletedComments,
    totalComments: createSelector(
      selectNonDeletedComments,
      (comments) => comments.length,
    ),
    heartbeatInfo: selectHeartbeatInfo,
    heartbeatEnabled: selectHeartbeatEnabled,
  })).pipe(
      map(vm => ({
          ...vm,
          heartbeatMenuItems: this.getMenuItems(vm.heartbeatInfo, vm.heartbeatEnabled),
          aiIsDisabled: vm.heartbeatInfo.aiDisabled !== null
            && vm.heartbeatInfo.aiDisabled !== undefined
            && !(vm.heartbeatInfo.aiDisabled !== -1 && vm.heartbeatInfo.aiDisabled <= currentTimeSeconds()),
      }))
  );

  constructor(
    private store: Store,
    private actions$: Actions,
  ) {
    this.actions$.pipe(
      ofType(JobActivityActions.updateHeartbeatRequested),
      takeUntil(this.destroy$),
    ).subscribe(() => { this.heartbeatLoading = true; });

    this.actions$.pipe(
      ofType(JobActivityActions.updateHeartbeatSuccess, JobActivityActions.updateHeartbeatError),
      takeUntil(this.destroy$),
    ).subscribe(() => { this.heartbeatLoading = false; });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  identifyComment(_index: number, comment: EditableComment) {
    return comment.id;
  }

  resumeAI(jobId: string) {
    this.store.dispatch(JobActivityActions.updateHeartbeatRequested({
      jobId,
      aiDisabled: null,
    }));
  }

  getMenuItems(info: {
    lastHeartbeat?: number;
    nextHeartbeat?: number;
    aiDisabled?: number;
    heartbeatMessage?: string;
    heartbeatCost?: number;
    heartbeatCount?: number;
    jobId: string;
    timezone: string;
  }, heartbeatEnabled: boolean): MenuItem[] {
    if (!info.jobId) return [];

    const aiEnabled = info.aiDisabled === null || info.aiDisabled === undefined
      || (info.aiDisabled !== -1 && info.aiDisabled <= currentTimeSeconds());

    const items: MenuItem[] = [];

    if (heartbeatEnabled) {
      const lastHeartbeatStr = info.lastHeartbeat
        ? formatDateTimeWithTimezone(info.lastHeartbeat, info.timezone).date + ' ' +
          formatDateTimeWithTimezone(info.lastHeartbeat, info.timezone).time
        : 'Never';

      const nextHeartbeatStr = info.nextHeartbeat
        ? formatDateTimeWithTimezone(info.nextHeartbeat, info.timezone).date + ' ' +
          formatDateTimeWithTimezone(info.nextHeartbeat, info.timezone).time
        : 'Pending';

      const heartbeatItems: MenuItem[] = [
        {
          label: `Last Check: ${lastHeartbeatStr}`,
          tooltip: info.lastHeartbeat ? dayjs.unix(info.lastHeartbeat).fromNow() : '',
          disabled: true,
          styleClass: 'text-sm text-color-secondary font-normal'
        },
        {
          label: `Next Check: ${nextHeartbeatStr}`,
          tooltip: info.nextHeartbeat ? dayjs.unix(info.nextHeartbeat).fromNow() : '',
          disabled: true,
          styleClass: 'text-sm text-color-secondary font-normal'
        },
        {
          label: this.heartbeatLoading ? 'Running Heartbeat...' : 'Run Heartbeat Now',
          icon: this.heartbeatLoading ? 'pi pi-spin pi-spinner' : 'pi pi-bolt',
          styleClass: this.heartbeatLoading ? 'font-bold p-disabled' : 'font-bold',
          disabled: this.heartbeatLoading,
          command: () => this.store.dispatch(JobActivityActions.updateHeartbeatRequested({
            jobId: info.jobId,
            nextHeartbeat: currentTimeSeconds(),
            aiDisabled: null
          }))
        },
      ];

      if (info.heartbeatCost != null && info.heartbeatCost > 0) {
        heartbeatItems.push({
          label: `Cost: $${info.heartbeatCost.toFixed(2)} (${info.heartbeatCount || 0} checks)`,
          disabled: true,
          styleClass: 'text-sm text-color-secondary font-normal',
        });
      }

      items.push({
        label: 'Heartbeat',
        items: heartbeatItems,
      });

      items.push({ separator: true });
    } else {
      items.push({
        label: 'Enable Heartbeat',
        icon: 'pi pi-bolt',
        styleClass: 'font-bold',
        command: () => this.store.dispatch(JobActivityActions.updateHeartbeatRequested({
          jobId: info.jobId,
          nextHeartbeat: currentTimeSeconds(),
          aiDisabled: null
        }))
      });

      items.push({ separator: true });
    }

    if (aiEnabled) {
      items.push({
        label: 'Disable AI',
        icon: 'pi pi-ban',
        items: [
          {
            label: 'For 1 Hour',
            command: () => this.store.dispatch(JobActivityActions.updateHeartbeatRequested({
              jobId: info.jobId,
              aiDisabled: currentTimeSeconds() + (60 * 60)
            }))
          },
          {
            label: 'For 1 Day',
            command: () => this.store.dispatch(JobActivityActions.updateHeartbeatRequested({
              jobId: info.jobId,
              aiDisabled: currentTimeSeconds() + (24 * 60 * 60)
            }))
          },
          {
            label: 'Permanently',
            command: () => this.store.dispatch(JobActivityActions.updateHeartbeatRequested({
              jobId: info.jobId,
              aiDisabled: -1
            }))
          }
        ]
      });
    } else {
      // AI is disabled — show disabled label inside menu, but Resume AI
      // is rendered as a standalone button in the template
      const isPermanent = info.aiDisabled === -1;
      const disabledLabel = isPermanent
        ? 'AI Disabled: Permanently'
        : `AI Disabled: ${formatDateTimeWithTimezone(info.aiDisabled!, info.timezone).time}`;

      items.push({
        label: disabledLabel,
        disabled: true,
        styleClass: 'text-sm text-color-secondary font-normal',
      });
    }

    return items;
  }
}
